import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { Button } from '@m/alchemy-ui/Button';
import { Toast } from '@m/alchemy-ui/Toast';
import { type ToastMessageProps } from '@m/alchemy-ui/ToastMessage';
import { type AppProps } from '../../common/interfaces';
import { GET_STATUS_PAGE_BY_COMMUNITY_ID } from '../../graphql/queries/status-page';
import { type CommunityRemovalStatusQuery } from '../../graphql/queries/__generated__/status-page.generated';
import { COMMUNITY_STATUS_RESET } from '../../graphql/mutations/community-status-reset';
import { type ResetCommunityRemovalStatusMutation } from '../../graphql/mutations/__generated__/community-status-reset.generated';
import { CommunityRemovalStatus } from '../../common/enums/CommunityRemovalStatus';
import { getErrorToastMessage } from '../../helpers';
import { StatusErrorCard } from './components/StatusErrorCard';
import { StatusTable } from './components/StatusTable';
import { StatusReport } from './components/Report/StatusReport';

export const StatusPage = (props: AppProps) => {
  const { community } = props;
  const showAdvancedReportCaption = 'Show advanced report';

  const { data, refetch } = useQuery<CommunityRemovalStatusQuery>(GET_STATUS_PAGE_BY_COMMUNITY_ID, {
    variables: { communityId: Number(community.id), isAdvanced: false }
  });

  const [resetCommunityStatus] = useMutation<ResetCommunityRemovalStatusMutation>(COMMUNITY_STATUS_RESET);

  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<ToastMessageProps[]>([]);

  const resetButtonClick = async () => {
    try {
      const { data } = await resetCommunityStatus({
        variables: { communityId: Number(community.id) }
      });

      if (data?.resetCommunityRemovalStatus) {
        refetch();
      }
    } catch (err) {
      console.error('Cannot reset process step:', err);
    }
  };

  const showAdvancedReportClick = async () => {
    setIsLoading(true);

    try {
      await refetch({
        communityId: Number(community.id),
        isAdvanced: true
      });
    } catch (error) {
      setToastMessage(getErrorToastMessage('Cannot get advanced report'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!data?.communityRemovalStatus) {
    return null;
  }

  const { error, report, ...others } = data.communityRemovalStatus;
  const canShowBasicReport = report && Array.isArray(report.init) && report.init.length > 0;
  const canShowAdvancedReport = canShowBasicReport && report.end && Array.isArray(report.end) && report.end.length > 0;

  return (
    <div data-testid="status-page">
      <StatusTable {...others} resetButtonClick={resetButtonClick} />
      {error && <StatusErrorCard error={error} />}
      {canShowBasicReport ? (
        <>
          <StatusReport {...others} report={report} />
          {canShowAdvancedReport ? null : (
            <Button
              priority="secondary"
              onClick={showAdvancedReportClick}
              isLoading={isLoading}
              disabled={!(data.communityRemovalStatus.overallStatus === CommunityRemovalStatus.Completed)}
            >
              {showAdvancedReportCaption}
            </Button>
          )}
        </>
      ) : null}

      {canShowAdvancedReport ? <StatusReport {...others} report={report} isAdvanced /> : null}
      <Toast messages={toastMessage} />
    </div>
  );
};
